import { parseData, findDependants, findDeliveriesFromList, allowSimReEntry } from './helpers';
import Reports from './reports';
import Errors from './errors';
import time from './timer';
import chat from './chat';
import email from './email';
import { ASSESSMENT_TYPES } from './constants';

export default {
  state: {
    user: {},
    emails: [],
    messages: [],
    files: [],
    folders: [],
    userData: {},
    cycle: null,
    assessmentComplete: false,
    assetsPath: '',
    selectedQuestionID: 1,
    recentlySelectedAnswerKey: null,
    emailsLog: [],
    assessmentTypeKey: null,
    dashboardAccess: {
      accessStartDate: null,
      accessEndDate: null,
    },
    role: {},
    versionConfig: {},
    simsettings: {},
    theme: {
      isPreset: null,
      props: {},
    },
    demoEmail: null,
    versionTemplateKey: null,
    isStateLoading: false,
  },

  modules: {
    time,
    chat,
    email,
    Reports,
    Errors,
  },

  actions: {
    CHECK_NEW_DELIVERIES: async ({ state, getters, dispatch, commit }) => {
      const emailsToDeliver = findDeliveriesFromList(
        getters['email/activeEmails'],
        getters['time/elapsed'],
        state.emailsLog
      );
      const messagesToDeliver = findDeliveriesFromList(
        getters['chat/activeMessages'],
        getters['time/elapsed'],
        state.emailsLog
      );

      if (emailsToDeliver.length) {
        await dispatch('email/DELIVER_EMAILS', emailsToDeliver);
      }
      if (messagesToDeliver.length) {
        await dispatch('chat/DELIVER_MESSAGES', messagesToDeliver);
      }

      // email.answer refers to user answer. email.answers (plural) is the list of answers available for this question
      // TODO: Update names for answers and answer so it's easy to differentiate them
      const hasRemainingUnansweredEmails = state.email.emails.filter(
        (email) =>
          email.isDelivered &&
          ((!email.isWrittenResponse && email.answers.length && !email.answer) ||
            (email.isWrittenResponse && email.writtenResponse == ''))
      ).length;

      const hasRemainingUnansweredMessages = state.chat.deliveredMessages.filter(
        (message) =>
          message.isDelivered &&
          ((!message.isWrittenResponse && message.answers.length && !message.answer) ||
            (message.isWrittenResponse && message.writtenResponse == ''))
      ).length;

      // If user answered through all the currently available emails, we deliver the next timed one immediately as opposed to letting the user wait for it
      if (
        (getters['email/emailsCompleted'] || !hasRemainingUnansweredEmails) &&
        !hasRemainingUnansweredMessages
      ) {
        const sorting = (a, b) => {
          return a.timer - b.timer;
        };

        const nextImmediateEmail = getters['email/cleanEmails'].sort(sorting).find((email) => {
          return email.timer > getters['time/elapsed'];
        });

        if (nextImmediateEmail) {
          commit('email/EXPEDITE_EMAIL', {
            email: nextImmediateEmail,
            time: getters['time/elapsed'],
          });
          return;
        }
        const nextImmediateMessage = getters['chat/unansweredActiveMessages']
          .sort(sorting)
          .find((message) => message.timer > getters['time/elapsed']);

        if (nextImmediateMessage) {
          // Expedite the timer for the message
          commit('chat/EXPEDITE_MESSAGE', {
            message: nextImmediateMessage,
            time: getters['time/elapsed'],
          });
        }
      }
    },

    SET_PHASE_DEPENDANT_QUESTIONS: ({ commit, rootGetters, rootState }) => {
      let answers = rootState.studentAnswerLog;
      if (typeof answers != 'undefined') {
        const dependantMessages = findDependants(rootGetters['chat/messagesFromThread'], answers);
        const dependantEmails = findDependants(rootGetters['email/threadEmails'], answers);
        commit('email/SET_DEPENDANT', {
          dependantEmails,
          rootGetters,
          time: { minutes: rootState.time.minutes, hour: rootState.time.hour },
        });
        commit('chat/SET_DEPENDANT', { dependantMessages, rootGetters }, { root: true });
      }
    },
    SET_USER: ({ commit }, user) => {
      return new Promise((resolve, reject) => {
        $.ajax({
          url: '/capsiminbox/demo/set-user',
          method: 'post',
          data: JSON.stringify(user),
          contentType: 'application/json',
        })
          .done(() => {
            commit('SET_USER', user);
            resolve();
          })
          .fail((err) => reject(err));
      });
    },

    LOAD_STATE: ({ commit, state }) => {
      state.isStateLoading = true;
      if (state.assessmentTypeKey == ASSESSMENT_TYPES.WEBAPP) {
        return new Promise(function (resolve, reject) {
          $.ajax({
            url: '/capsiminbox/webapp/data',
            method: 'get',
            dataType: 'json',
          })
            .done((rawData) => {
              const data = parseData(
                rawData,
                state.userData,
                state.versionConfig,
                rawData.simsettings
              );

              data.emails.forEach((email) => {
                if (email.FK_questionDisplayTypeKey === 3) {
                  const emails = Object.values(data.emails).filter((question) => {
                    return question.feedbackKey === email.questionKey;
                  });

                  if (emails.length > 0) {
                    const originalEmail = emails[0];
                    email.subjectTagKey =
                      originalEmail.authorNameTagKey + ' - ' + originalEmail.subjectTagKey;
                    email.descriptionTagKey =
                      '<strong>You just responded to…</strong></br></br>' +
                      originalEmail.descriptionTagKey;
                    email.files = [...email.files, ...originalEmail.files];
                    email.answers = [...email.answers, ...originalEmail.answers];
                  }
                }
              });

              const emails = data.emails.filter((email) =>
                data.emailsLog.includes(email.questionKey)
              );
              const deliveredMessages = data.messages.filter((message) =>
                data.emailsLog.includes(message.questionKey)
              );
              state.isStateLoading = false;
              commit('SET_STATE', { data });
              commit('chat/SET_MESSAGES', data.messages);
              commit('email/SET_EMAILS', data.emails);

              commit('email/DELIVER_EMAILS', emails);
              commit('chat/DELIVER_MESSAGES', deliveredMessages);
              resolve();
            })
            .fail((err) => reject(err));
        });
      } else if (state.assessmentTypeKey == ASSESSMENT_TYPES.REHEARSAL) {
        return new Promise(function (resolve, reject) {
          $.ajax({
            url: '/capsiminbox/rehearsal/data',
            method: 'get',
            dataType: 'json',
          })
            .done((rawData) => {
              const data = parseData(rawData, state.userData, state.versionConfig);

              data.emails.forEach((email) => {
                if (email.FK_questionDisplayTypeKey === 3) {
                  const emails = Object.values(data.emails).filter((question) => {
                    return question.feedbackKey === email.questionKey;
                  });

                  if (emails.length > 0) {
                    const originalEmail = emails[0];
                    email.subjectTagKey =
                      originalEmail.authorNameTagKey + ' - ' + originalEmail.subjectTagKey;
                    email.descriptionTagKey =
                      '<strong>You just responded to…</strong></br></br>' +
                      originalEmail.descriptionTagKey;
                    email.files = [...email.files, ...originalEmail.files];
                    email.answers = [...email.answers, ...originalEmail.answers];
                  }
                }
              });
              commit('SET_STATE', { data });
              commit('chat/SET_MESSAGES', data.messages);
              commit('email/SET_EMAILS', data.emails);
              resolve();
            })
            .fail((err) => reject(err));
        });
      } else if (state.assessmentTypeKey == ASSESSMENT_TYPES.DEMO) {
        // parseData(state, state.userData)
        // commit('chat/SET_MESSAGES', state.messages)
        // commit('email/SET_EMAILS', state.emails)

        return new Promise(function (resolve, reject) {
          $.ajax({
            url: '/capsiminbox/demo/data',
            method: 'get',
            dataType: 'json',
          })
            .done((rawData) => {
              const data = parseData(rawData, state.userData, state.versionConfig);

              data.emails.forEach((email) => {
                if (email.FK_questionDisplayTypeKey === 3) {
                  const emails = Object.values(data.emails).filter((question) => {
                    return question.feedbackKey === email.questionKey;
                  });

                  if (emails.length > 0) {
                    const originalEmail = emails[0];
                    email.subjectTagKey =
                      originalEmail.authorNameTagKey + ' - ' + originalEmail.subjectTagKey;
                    email.descriptionTagKey =
                      '<strong>You just responded to…</strong></br></br>' +
                      originalEmail.descriptionTagKey;
                    email.files = [...email.files, ...originalEmail.files];
                    email.answers = [...email.answers, ...originalEmail.answers];
                  }
                }
              });
              commit('SET_STATE', { data });
              commit('chat/SET_MESSAGES', data.messages);
              commit('email/SET_EMAILS', data.emails);
              resolve();
            })
            .fail((err) => reject(err));
        });
      } else if (state.assessmentTypeKey == ASSESSMENT_TYPES.PREVIEW) {
        // parseData(state, state.userData)
        // commit('chat/SET_MESSAGES', state.messages)
        // commit('email/SET_EMAILS', state.emails)
        return new Promise(function (resolve, reject) {
          $.ajax({
            url: '/capsiminbox/authoring-platform/data',
            method: 'get',
            dataType: 'json',
          })
            .done((rawData) => {
              const data = parseData(rawData, state.userData, state.versionConfig);

              data.emails.forEach((email) => {
                if (email.FK_questionDisplayTypeKey === 3) {
                  const emails = Object.values(data.emails).filter((question) => {
                    return question.feedbackKey === email.questionKey;
                  });

                  if (emails.length > 0) {
                    const originalEmail = emails[0];
                    email.subjectTagKey =
                      originalEmail.authorNameTagKey + ' - ' + originalEmail.subjectTagKey;
                    email.descriptionTagKey =
                      '<strong>You just responded to…</strong></br></br>' +
                      originalEmail.descriptionTagKey;
                    email.files = [...email.files, ...originalEmail.files];
                    email.answers = [...email.answers, ...originalEmail.answers];
                  }
                }
              });

              commit('SET_STATE', { data });
              commit('chat/SET_MESSAGES', data.messages);
              commit('email/SET_EMAILS', data.emails);
              resolve();
            })
            .fail((err) => reject(err));
        });
      }
    },

    GET_ACCESS: ({ commit, state }) => {
      return new Promise(function (resolve, reject) {
        $.ajax({
          url: '/capsiminbox/student/get-student-access',
          method: 'get',
          dataType: 'json',
        })
          .done((rawData) => {
            const { dashboardAccess } = rawData;
            commit('SET_ACCESS', dashboardAccess);
          })
          .fail((err) => {
            if (err.status === 403) {
              window.location.href = '/capsiminbox/student/logout';
              return;
            }
            reject(err);
          });
      });
    },

    START_SIM: () =>
      $.post({ url: '/capsiminbox/webapp/log-activity', dataType: 'json', data: { actionKey: 1 } }),
  },

  mutations: {
    SET_USER: (state, userData) => Object.assign(state.userData, userData),

    SET_STATE: (state, { data }) => Object.assign(state, data),

    // SET_STATE not being reactive
    SET_ACCESS: (state, data) => {
      state.dashboardAccess = data;
    },

    SET_APP: (state) => Object.assign(state, window.data),
  },

  getters: {
    allowInboxSimReEntry: (state) => allowSimReEntry(state.simsettings),
    // Original Data
    showTimerAsClock: (state) =>
      state.versionConfig ? state.versionConfig.showTimerAsClock : false,

    // Modified Data
    assessmentComplete: (state, getters) => {
      return (
        getters['email/emailsCompleted'] &&
        getters['chat/messagesCompleted'] &&
        getters['time/elapsed'] > 5000
      );
    },

    unreadMessages: (state) => state.messages.filter((message) => message.isRead === true).length,

    timer: (state) => state.timer,

    // Constants
    constants: () => ({
      'chat-width': 280,
      'expanded-chat-width': 530,
      ASSESSMENT_TYPE_KEY: ASSESSMENT_TYPES,
    }),

    assessmentType: (state, getters) => {
      return {
        isWebApp: state.assessmentTypeKey == ASSESSMENT_TYPES.WEBAPP,
        isPreview: state.assessmentTypeKey == ASSESSMENT_TYPES.PREVIEW,
        isDemo: state.assessmentTypeKey == ASSESSMENT_TYPES.DEMO,
        isRehearsal: state.assessmentTypeKey == ASSESSMENT_TYPES.REHEARSAL,
      };
    },
  },
};
